import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CartItem, CartService } from '../../services/cart.service';
import { ProductResponse } from '../../services/api.service';
import { LanguageService, Language } from '../../services/language.service';
import { AnalyticsService } from '../../services/analytics.service';
import { CurrencyService } from '../../services/currency.service';
import { ProductSheetService } from '../../services/product-sheet.service';
import { SupplementCatalogService } from '../../services/supplement-catalog.service';
import {
  SupplementTileIntent,
  productCartLines,
  routeSupplementTileIntent
} from '../../shared/supplement-tile-routing';

@Component({
    selector: 'app-most-ordered-item',
    templateUrl: './most-ordered-item.component.html',
    styleUrls: ['./most-ordered-item.component.scss'],
    standalone: false
})
export class MostOrderedItemComponent implements OnInit, OnDestroy, OnChanges {

  @Input() currencySymbol!: string;
  @Input() product: any;
  @Input() mainView!: boolean;
  @Input() comesFrom!: string;
  currentLang: string = '';

  // Image loading state — drives the spinner over the product image tile.
  imageLoaded = false;

  cartItems: CartItem[] = [];
  clientData: any = null;
  private refreshInterval?: any;
  private lastClientDataString: string = '';
  /**
   * Both long-lived subscriptions, torn down together in ngOnDestroy.
   *
   * This tile is re-created on every visit to the menu and the featured strip renders a dozen of
   * them, so an unterminated cart subscription does not merely leak the component: each dead
   * instance would keep re-running `refreshCartLines()` — a filter plus a key-building sort over the
   * whole cart — on every single `+` the guest taps anywhere in the app.
   */
  private subscriptions = new Subscription();

  /**
   * This product's cart lines, in the one deterministic order every add-on surface uses.
   *
   * Cached per cart emission rather than derived in a getter: the featured strip renders a dozen of
   * these and the getter would re-filter and re-sort the cart on every change-detection pass.
   */
  cartLines: CartItem[] = [];

  constructor(
    private router: Router,
    private cartService: CartService,
    private languageService: LanguageService,
    private analytics: AnalyticsService,
    private currencyService: CurrencyService,
    private cdr: ChangeDetectorRef,
    private productSheet: ProductSheetService,
    private supplementCatalog: SupplementCatalogService
  ) { }

  onProductClick(productId: number) {
    // Determine entry screen based on comesFrom input
    let entryScreen = 'menu';
    if (this.comesFrom === 'most-ordered') {
      entryScreen = 'menu'; // Most ordered is part of menu
    }
    
    this.analytics.log('product_open', {
      product_id: productId.toString(),
      product_name: this.product.name,
      entry_screen: entryScreen
    });

    this.productSheet.open(productId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A new product bound to this instance means its image reloads — show the spinner again.
    if (changes['product'] && !changes['product'].firstChange) {
      this.imageLoaded = false;
      // The strip recycles these tiles across products, so the lines cached for the previous one
      // would otherwise drive this one's badge until the cart next emitted.
      this.refreshCartLines();
    }
  }

  onImageLoad(): void { this.imageLoaded = true; }
  onImageError(): void { this.imageLoaded = true; }

  addToCart(event: Event, product: any) {
    event.stopPropagation(); // Prevent navigation
    this.runTileIntent('increment', () => this.cartService.addToCart(product, this.comesFrom));
  }

  isInCart(productId: number): boolean {
    return this.getItemQuantity(productId) > 0;
  }

  /**
   * The quantity badge: the SUM across every line of this dish, not the first line that happens to
   * match. A guest holding two cheese pizzas and one bacon pizza has ordered three of it, and the
   * old `find(i => i.id === productId)` showed them two.
   */
  getItemQuantity(productId: number): number {
    return this.cartService.getProductQuantity(productId);
  }

  /**
   * How many distinct add-on selections of this dish are in the order. Only ever above 1 for a
   * product with add-ons, so no catalogue lookup is needed to decide whether to draw the chip.
   */
  get combinationCount(): number {
    return this.cartLines.length;
  }

  updateQuantity(event: Event, product: ProductResponse, change: number): void {
    event.stopPropagation();
    this.runTileIntent(change > 0 ? 'increment' : 'decrement', () => {
      const currentQuantity = this.getItemQuantity(product.id);
      if (currentQuantity + change <= 0) {
        this.cartService.removeFromCart(product.id, this.comesFrom);
      } else {
        this.cartService.updateQuantity(product.id, currentQuantity + change, 'menu');
      }
    });
  }

  /** The chip opens the same picker `+` does — "which of these did you mean" is the same question. */
  onCombinationsTap(event: Event): void {
    event.stopPropagation();
    this.onProductClick(this.product.id);
  }

  /**
   * The 0 / 1 / 2+ branch, decided by the shared router so this tile and the full-width list tile
   * cannot drift apart on it.
   */
  private runTileIntent(intent: SupplementTileIntent, fallback: () => void): void {
    const action = routeSupplementTileIntent(
      intent,
      this.supplementCatalog.productHasSupplements(this.product),
      this.cartLines
    );

    switch (action.kind) {
      case 'fallback':
        // No add-ons: this tile's own handler runs untouched, analytics source strings and all.
        fallback();
        return;

      case 'openSheet':
        // Always in ADD mode, through the same path a tap on the card body takes, so `product_open`
        // is still logged: pointing at one existing line is what the sheet's own list is for.
        this.onProductClick(this.product.id);
        return;

      case 'adjustLine': {
        const lineKey = this.cartService.getLineKey(action.line);
        if (action.delta === 'reset') {
          this.cartService.removeLine(lineKey, this.comesFrom);
        } else {
          // `updateLineQuantity` re-applies the availability guard when the number goes UP and
          // routes to `removeLine` when it reaches zero, so skipping the sheet here is not a way
          // around either — which is why the fast path needs no gate of its own.
          this.cartService.updateLineQuantity(
            lineKey,
            action.line.quantity + action.delta,
            'menu'
          );
        }
        return;
      }

      case 'none':
        // The stepper is not rendered at zero, so a `−` that lands here has no line to act on.
        return;
    }
  }

  private refreshCartLines(): void {
    this.cartLines = productCartLines(this.cartItems, this.product?.id ?? -1);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.languageService.currentLang$.subscribe(lang => {
        this.currentLang = lang;
      })
    );

    this.subscriptions.add(
      this.cartService.cartItems$.subscribe(items => {
        this.cartItems = items;
        this.refreshCartLines();
      })
    );

    // Load clientData for second currency checks
    this.loadClientData();

    // Set up periodic check for clientData updates (in case second currency settings change)
    this.refreshInterval = setInterval(() => {
      this.checkForClientDataUpdates();
    }, 1000);
  }

  ngOnDestroy(): void {
    // Clear interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    this.subscriptions.unsubscribe();
  }

  private loadClientData(): void {
    const clientDataString = sessionStorage.getItem('clientData');
    if (clientDataString) {
      try {
        this.clientData = JSON.parse(clientDataString);
        this.lastClientDataString = JSON.stringify(this.clientData);
      } catch (e) {
        console.error('Error parsing clientData:', e);
      }
    }
  }

  private checkForClientDataUpdates(): void {
    const clientDataString = sessionStorage.getItem('clientData');
    if (clientDataString) {
      try {
        const newClientData = JSON.parse(clientDataString);
        const newClientDataString = JSON.stringify(newClientData);
        
        // If clientData has changed (e.g., second currency settings), update the UI
        if (newClientDataString !== this.lastClientDataString) {
          this.clientData = newClientData;
          this.lastClientDataString = newClientDataString;
          // Trigger change detection to update UI (same pattern as menu component)
          this.cdr.detectChanges();
        }
      } catch (error) {
        console.error('Error parsing clientData from sessionStorage:', error);
      }
    }
  }

  hasValidImage(product: any): boolean {
    return !!(product.images && product.images.length > 0 && product.images[0] && product.images[0].src);
  }

  getProductImageSrc(product: any): string {
    if (this.hasValidImage(product)) {
      return product.images[0].src;
    }
    return '../../assets/ImageBroken.svg';
  }

  getProductName(product: ProductResponse, _lang?: string): string {
    return this.languageService.localizeMetaValue(product.meta_data, '', product.name);
  }

  getSecondPrice(price: string | number): string | null {
    return this.currencyService.getSecondPrice(price, this.clientData);
  }

  getSecondCurrencySymbol(): string | null {
    return this.currencyService.getSecondCurrencySymbol(this.clientData);
  }

  isSecondCurrencyActive(): boolean {
    return this.currencyService.isSecondCurrencyActive(this.clientData);
  }

  canEnableOrdering(): boolean {
    const orderingType = sessionStorage.getItem('orderingType');
    
    // Rule 1: Table QR (dine-in)
    if (orderingType === 'dine-in') {
      return this.clientData?.feature_flags?.ft_orders_from_table === true &&   
             this.isOrderingSupportedByExistingLogic();
    }
    
    // Rule 2: Main QR or Order Location (off-site/delivery)
    if (orderingType === 'off-site' || orderingType === 'delivery') {
      return this.clientData?.feature_flags?.ft_orders_from_home === true && 
             this.isOrderingSupportedByExistingLogic();
    }
    
    return false;
  }

  private isOrderingSupportedByExistingLogic(): boolean {
    // Match iOS isOrderingEnabled exactly: is_ordering_active must be true (nil/false → false), is_open_now/is_order_supported nil = OK.
    return this.clientData?.is_ordering_active === true &&
           (this.clientData?.is_open_now !== false) &&
           (this.clientData?.is_order_supported !== false);
  }

}
